/*
  GovernanceConfigWidget — Phase 8 (TAF-012). Clinical-lead controls
  for AI transcript extraction:

    · Per-class minimum confidence threshold (0.0 – 1.0).
    · Class D "dictated objective result" opt-in toggle.

  Changes take effect on the next extraction call. Config is a
  singleton (governance_config collection, _id="singleton") so
  concurrent edits are last-write-wins — acceptable for a low-
  frequency admin surface.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SlidersHorizontal, RefreshCcw, RotateCcw } from "lucide-react";


const CLASS_META = [
  { cls: "A", label: "Patient narrative" },
  { cls: "B", label: "Factual history" },
  { cls: "C", label: "Clinician observation" },
  { cls: "D", label: "Objective result" },
  { cls: "E", label: "Derived score" },
  { cls: "F", label: "Restricted decision" },
];


export default function GovernanceConfigWidget() {
  const [cfg, setCfg] = useState(null);
  const [thresholds, setThresholds] = useState({});
  const [classD, setClassD] = useState(false);
  const [audioDays, setAudioDays] = useState(90);
  const [transcriptDays, setTranscriptDays] = useState(365);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/governance/config");
      setCfg(r.data);
      setThresholds(r.data.confidence_thresholds || {});
      setClassD(!!r.data.class_d_enabled);
      setAudioDays(r.data.audio_retention_days ?? 90);
      setTranscriptDays(r.data.transcript_retention_days ?? 365);
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load governance config", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function setThreshold(cls, val) {
    setThresholds((prev) => ({ ...prev, [cls]: val }));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      await api.put("/governance/config", {
        confidence_thresholds: thresholds,
        class_d_enabled: classD,
        audio_retention_days: audioDays,
        transcript_retention_days: transcriptDays,
      });
      toast.success("Governance config saved");
      await load();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  async function runSweep(dryRun) {
    setSweeping(true);
    try {
      const r = await api.post(
        `/governance/retention/run-sweep?dry_run=${dryRun}`,
      );
      const d = r.data;
      if (dryRun) {
        toast.success(
          `Dry run — ${d.audio_candidates ?? 0} audio + ${d.transcript_candidates ?? 0} transcripts would be deleted`,
        );
      } else {
        toast.success(
          `Deleted ${d.audio_deleted?.length ?? 0} audio + ${d.transcript_deleted?.length ?? 0} transcripts`,
        );
      }
    } catch (e) {
      toast.error("Retention sweep failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSweeping(false); }
  }

  function resetToDefaults() {
    if (!cfg?.defaults) return;
    setThresholds({ ...cfg.defaults.confidence_thresholds });
    setClassD(!!cfg.defaults.class_d_enabled);
    setAudioDays(cfg.defaults.audio_retention_days ?? 90);
    setTranscriptDays(cfg.defaults.transcript_retention_days ?? 365);
    setDirty(true);
  }

  return (
    <Card className="p-4" data-testid="governance-config-widget">
      <div className="flex items-center gap-2 border rounded-md px-3 py-2 mb-3 border-slate-200 bg-slate-50 text-slate-900">
        <SlidersHorizontal className="h-4 w-4 shrink-0" />
        <div className="font-semibold text-sm">
          Extraction thresholds &amp; Class D toggle
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm" variant="ghost"
            className="h-6 px-2 text-slate-800 hover:bg-slate-200"
            onClick={load}
            data-testid="gov-config-refresh"
            title="Reload from server"
          >
            <RefreshCcw className="h-3 w-3" />
          </Button>
          <Button
            size="sm" variant="ghost"
            className="h-6 px-2 text-slate-800 hover:bg-slate-200"
            onClick={resetToDefaults}
            data-testid="gov-config-reset-defaults"
            title="Reset to spec defaults (not yet saved)"
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Thresholds */}
        <div>
          <div className="text-2xs uppercase tracking-wider text-slate-600 font-semibold mb-2">
            Minimum confidence per class
          </div>
          <div className="space-y-2">
            {CLASS_META.map(({ cls, label }) => {
              const val = thresholds[cls] ?? 0;
              const dflt = cfg?.defaults?.confidence_thresholds?.[cls] ?? 0;
              const drifted = Math.abs(val - dflt) > 0.005;
              return (
                <div key={cls} className="flex items-center gap-2 text-xs">
                  <div className="w-8 shrink-0">
                    <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-900">
                      {cls}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <input
                      type="range"
                      min={0} max={1} step={0.05}
                      value={val}
                      onChange={(e) => setThreshold(cls, Number(e.target.value))}
                      className="w-full"
                      data-testid={`gov-threshold-slider-${cls}`}
                    />
                    <div className="text-2xs text-slate-500 truncate">{label}</div>
                  </div>
                  <div className={`w-16 text-right font-mono-num ${drifted ? "text-amber-800 font-bold" : "text-slate-800"}`}>
                    {(val * 100).toFixed(0)}%
                    {drifted && (
                      <span className="ml-1 text-2xs text-amber-700"
                              title={`Default ${(dflt * 100).toFixed(0)}%`}>
                        Δ
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-2xs text-slate-500 mt-2">
            Suggestions with a numeric confidence below the class threshold
            are dropped at extraction time and never surfaced to the reviewer.
          </div>
        </div>

        {/* Class D toggle */}
        <div>
          <div className="text-2xs uppercase tracking-wider text-slate-600 font-semibold mb-2">
            Class D: dictated objective results
          </div>
          <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-md bg-white">
            <Switch
              checked={classD}
              onCheckedChange={(v) => { setClassD(!!v); setDirty(true); }}
              data-testid="gov-class-d-toggle"
            />
            <div className="text-xs">
              <div className="font-semibold text-slate-900">
                {classD ? "Enabled" : "Disabled (pilot default)"}
              </div>
              <div className="text-slate-600 mt-0.5">
                When on, dictated objective results (BP, grip, TUG, ROM…)
                are routed to the review queue with structured-metadata
                gates (units + laterality on side-capable fields).
                Batch-accept is always off; a clinician adjudicates.
              </div>
            </div>
          </div>
          {cfg?.updated_at && (
            <div className="text-2xs text-slate-500 mt-2">
              Last edited {new Date(cfg.updated_at).toLocaleString()}
              {cfg.updated_by && <> by <b>{cfg.updated_by}</b></>}
            </div>
          )}
        </div>
      </div>

      {/* Retention (spec §10) */}
      <div className="mt-4 pt-3 border-t border-slate-200">
        <div className="text-2xs uppercase tracking-wider text-slate-600 font-semibold mb-2">
          Retention policy
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RetentionField
            label="Audio segments"
            hint="Raw .webm recordings on disk + object storage"
            value={audioDays}
            onChange={(v) => { setAudioDays(v); setDirty(true); }}
            testIdPrefix="audio"
          />
          <RetentionField
            label="Transcripts"
            hint="Whisper text + word-level segment timestamps"
            value={transcriptDays}
            onChange={(v) => { setTranscriptDays(v); setDirty(true); }}
            testIdPrefix="transcript"
          />
        </div>
        <div className="text-2xs text-slate-500 mt-2">
          Auto-sweep removes artefacts past the window; provenance metadata
          (session id, model, timestamps) is preserved for audit.
          Set 0 to disable auto-delete for that artefact.
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => runSweep(true)}
            disabled={sweeping}
            data-testid="retention-dry-run-btn"
          >
            Dry run
          </Button>
          <Button
            size="sm" variant="outline"
            className="border-rose-300 text-rose-800 hover:bg-rose-50"
            onClick={() => {
              if (!window.confirm(
                "Run retention sweep now? Artefacts past the window will be permanently deleted.",
              )) return;
              runSweep(false);
            }}
            disabled={sweeping}
            data-testid="retention-run-btn"
          >
            Run sweep now
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-200">
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
          className="bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          data-testid="gov-config-save"
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
        {dirty && (
          <span className="text-2xs text-amber-700" data-testid="gov-config-dirty">
            unsaved changes
          </span>
        )}
      </div>
    </Card>
  );
}


const RETENTION_PRESETS = [30, 90, 180, 365, 0];

function RetentionField({ label, hint, value, onChange, testIdPrefix }) {
  return (
    <div className="p-3 border border-slate-200 rounded-md bg-white">
      <div className="text-sm font-semibold text-slate-900">{label}</div>
      <div className="text-2xs text-slate-600 mt-0.5">{hint}</div>
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {RETENTION_PRESETS.map((n) => {
          const active = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`text-xs px-2 py-1 rounded border ${
                active
                  ? "border-slate-800 bg-slate-800 text-white"
                  : "border-slate-300 bg-white text-slate-800 hover:bg-slate-100"
              }`}
              data-testid={`retention-${testIdPrefix}-${n}d`}
            >
              {n === 0 ? "off" : `${n}d`}
            </button>
          );
        })}
        <input
          type="number"
          min={0}
          max={3650}
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="text-xs w-16 border border-slate-300 rounded px-1 py-1"
          data-testid={`retention-${testIdPrefix}-input`}
        />
        <span className="text-xs text-slate-600">days</span>
      </div>
    </div>
  );
}
